import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './WalletCardsLegacy.css';
import WalletCard from './WalletCard';
import CuriosityCards from './CuriosityCards';
import Runway from './Runway';
import ThreeDEarthSection from './ThreeDEarthSection';
import AirplaneCompare from './AirplaneCompare';

// --- Style and Layout Constants ---
const CARD_ASPECT_RATIO = 2.44;
const CARD_WIDTH = 1080;
const CARD_HEIGHT = CARD_WIDTH / CARD_ASPECT_RATIO;
const TITLE_REVEAL_HEIGHT = 55;
const BOTTOM_PADDING = 40;
const TOP_PADDING = 110;
const MAX_VISIBLE_BEHIND = 3; // <--- The new limit for visible stacked cards
const DEFAULT_CONTAINER_HEIGHT =
  CARD_HEIGHT + MAX_VISIBLE_BEHIND * TITLE_REVEAL_HEIGHT + BOTTOM_PADDING + TOP_PADDING;

export default function WalletCardsLegacy({
  planes,
  loading,
  error,
  onCardClick,
  onCuriosityReady,
  scrollTo,
  suppressLoadingStatus = false,
  comparisonPlanes = [],
  comparisonLoading = false,
  comparisonSelectionIds,
}) {
  const scrollTimeoutRef = useRef(0);
  const wheelRafRef = useRef(0);
  const wheelDirectionRef = useRef(0);
  const isMouseOver = useRef(false);
  const containerRef = useRef(null);
  const orderedPlanesRef = useRef(planes);
  const activePlaneIdRef = useRef(null);
  const [fallbackHeight] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_CONTAINER_HEIGHT;
    const savedHeight = window.sessionStorage.getItem('walletStackHeight');
    const parsed = Number(savedHeight);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_CONTAINER_HEIGHT;
  });

  useEffect(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = 0;
    }
    if (scrollTo) {
      const targetElement = document.querySelector(scrollTo);
      if (targetElement) {
        scrollTimeoutRef.current = window.setTimeout(() => {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.history.replaceState({}, document.title);
        }, 100);
      }
    }
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = 0;
      }
    };
  }, [scrollTo]);

  const [orderedPlanes, setOrderedPlanes] = useState(() => planes);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    orderedPlanesRef.current = orderedPlanes;
    activePlaneIdRef.current = orderedPlanes[activeIndex]?.id ?? null;
  }, [orderedPlanes, activeIndex]);

  useEffect(() => {
    if (!planes.length) {
      setOrderedPlanes([]);
      setActiveIndex(0);
      return;
    }
    const activeId = activePlaneIdRef.current;
    const nextById = new Map(planes.map((plane) => [plane.id, plane]));
    const prevOrder = orderedPlanesRef.current;
    const kept = prevOrder
      .filter((plane) => nextById.has(plane.id))
      .map((plane) => nextById.get(plane.id));
    const keptIds = new Set(kept.map((plane) => plane.id));
    const appended = planes.filter((plane) => !keptIds.has(plane.id));
    const merged = [...kept, ...appended];
    setOrderedPlanes(merged);
    if (activeId) {
      const nextIndex = merged.findIndex((plane) => plane.id === activeId);
      setActiveIndex(nextIndex === -1 ? 0 : nextIndex);
    } else {
      setActiveIndex(0);
    }
  }, [planes]);

  // Calculate dynamic container height based on the new limit
  const containerHeight = useMemo(() => {
    if (orderedPlanes.length === 0) return 0;
    const visibleStackCount = Math.min(orderedPlanes.length - 1, MAX_VISIBLE_BEHIND);
    const activeCardHeight = CARD_HEIGHT;
    const stackedCardsHeight = visibleStackCount * TITLE_REVEAL_HEIGHT;
    return activeCardHeight + stackedCardsHeight + BOTTOM_PADDING + TOP_PADDING;
  }, [orderedPlanes.length]);

  useEffect(() => {
    if (containerHeight <= 0) return;
    sessionStorage.setItem('walletStackHeight', String(containerHeight));
  }, [containerHeight]);
  

  // --- Event Handlers ---
  const isPointerOverContainer = useCallback((event) => {
    const container = containerRef.current;
    if (!container) return false;

    if (event?.target && container.contains(event.target)) {
      return true;
    }

    if (typeof event?.clientX === 'number' && typeof event?.clientY === 'number') {
      const elementAtPoint = document.elementFromPoint(event.clientX, event.clientY);
      if (elementAtPoint && container.contains(elementAtPoint)) {
        return true;
      }
    }

    return container.matches(':hover');
  }, []);

  const changeActiveIndex = useCallback((direction) => {
    if (!orderedPlanes.length) return;
    setActiveIndex(prev => (prev + direction + orderedPlanes.length) % orderedPlanes.length);
  }, [orderedPlanes.length]);

  const handleCardClick = useCallback((plane, index) => {
    if (index !== activeIndex) {
      setActiveIndex(index);
      return;
    }
    onCardClick(plane, index);
  }, [activeIndex, onCardClick]);
  

  // --- Global Listeners for Zoned Scrolling ---
  useEffect(() => {
    const handleWheel = (e) => {
      const isInside = isPointerOverContainer(e);
      if (!isInside) {
        isMouseOver.current = false;
        return;
      }
      isMouseOver.current = true;
      e.preventDefault();
      const direction = e.deltaY > 0 ? 1 : -1;
      wheelDirectionRef.current = direction;
      if (wheelRafRef.current) return;
      wheelRafRef.current = window.requestAnimationFrame(() => {
        wheelRafRef.current = 0;
        const dir = wheelDirectionRef.current;
        wheelDirectionRef.current = 0;
        if (dir) {
          changeActiveIndex(dir);
        }
      });
    };

    const handleKeyDown = (e) => {
      const isInside = isMouseOver.current || isPointerOverContainer();
      if (!isInside) return;
      isMouseOver.current = true;
      if (e.key === 'ArrowUp') { e.preventDefault(); changeActiveIndex(-1); }
      if (e.key === 'ArrowDown') { e.preventDefault(); changeActiveIndex(1); }
      if ((e.key === 'Enter' || e.key === ' ') && orderedPlanes[activeIndex]) {
        e.preventDefault();
        handleCardClick(orderedPlanes[activeIndex], activeIndex);
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
      if (wheelRafRef.current) {
        window.cancelAnimationFrame(wheelRafRef.current);
        wheelRafRef.current = 0;
      }
    };
  }, [activeIndex, orderedPlanes, changeActiveIndex, handleCardClick, isPointerOverContainer]);

  const containerClasses = ['apple-wallet-container'];
  const effectiveHeight = containerHeight > 0 ? containerHeight : loading || error ? fallbackHeight : 0;
  const showStatus = (loading && !suppressLoadingStatus) || Boolean(error);
  const statusText = loading ? '加载中...' : `加载失败：${error || '未知错误'}`;

  return (
    <>
      <div
        className={containerClasses.join(' ')}
        style={{ height: `${effectiveHeight}px`, '--stack-height': `${effectiveHeight}px` }} // Apply dynamic height
        ref={containerRef}
        onMouseEnter={() => { isMouseOver.current = true; }}
        onMouseLeave={() => { isMouseOver.current = false; }}
        tabIndex="-1"
      >
        {orderedPlanes.map((plane, index) => {
          const totalPlanes = orderedPlanes.length;
          let relativeIndex = index - activeIndex;
          if (totalPlanes > 0) {
            relativeIndex = ((relativeIndex % totalPlanes) + totalPlanes) % totalPlanes;
          }
          
          const isActive = relativeIndex === 0;
          const isBehind = relativeIndex > 0 && relativeIndex <= MAX_VISIBLE_BEHIND;
          const isInFront = relativeIndex > MAX_VISIBLE_BEHIND;

          const classes = ['wallet-card-snap'];
          if (isActive) classes.push('is-active');
          if (isBehind) classes.push('is-behind');
          if (isInFront) classes.push('is-in-front');

          return (
            <div 
              key={plane.id} 
              className={classes.join(' ')}
              style={{ '--i': relativeIndex }}
              onClick={() => handleCardClick(plane, index)}
            >
              <WalletCard 
                plane={plane} 
                isPreview={isBehind}
              />
            </div>
          );
        })}
        {showStatus && (
          <div className="wallet-cards-loading" role={loading ? 'status' : 'alert'}>
            {statusText}
          </div>
        )}
      </div>

      <Runway />
      <AirplaneCompare planes={comparisonPlanes} />
      <ThreeDEarthSection />
      <CuriosityCards onReady={onCuriosityReady} suppressLoadingStatus={suppressLoadingStatus} />
    </>
  );
}
